import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/FormPage.css';

const PermissionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resources, setResources] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    resource: '',
    action: '',
    description: ''
  });

  const actions = ['create', 'read', 'update', 'delete', 'manage', 'manage_hierarchy'];

  useEffect(() => {
    loadResources();
    if (isEditMode) {
      loadPermission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadResources = async () => {
    try {
      const response = await api.getPermissionResources();
      setResources(response.data || []);
    } catch (err) {
      console.error('Error loading resources:', err);
    }
  };

  const loadPermission = async () => {
    try {
      setLoading(true);
      const response = await api.getPermission(id);
      const permission = response.data;
      setFormData({
        name: permission.name || '',
        resource: permission.resource || '',
        action: permission.action || '',
        description: permission.description || ''
      });
    } catch (err) {
      setError('Failed to load permission');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Auto-generate name if resource and action are selected
    if (name === 'resource' || name === 'action') {
      const resource = name === 'resource' ? value : formData.resource;
      const action = name === 'action' ? value : formData.action;
      if (resource && action) {
        setFormData(prev => ({
          ...prev,
          name: `${resource}.${action}`,
          [name]: value
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (isEditMode) {
        await api.updatePermission(id, formData);
        setSuccess('Permission updated successfully!');
      } else {
        await api.createPermission(formData);
        setSuccess('Permission created successfully!');
      }

      setTimeout(() => {
        navigate('/permissions');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save permission');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/permissions');
  };

  if (loading && isEditMode) {
    return (
      <div className="form-page-container">
        <div className="loading">Loading permission...</div>
      </div>
    );
  }

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <div>
          <h1>{isEditMode ? 'Edit Permission' : 'Add New Permission'}</h1>
          <p className="form-page-subtitle">
            {isEditMode ? 'Update permission details' : 'Create a new permission for access control'}
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="form-section-title">Permission Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Resource *</label>
                <select
                  name="resource"
                  value={formData.resource}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Resource</option>
                  {resources.map(resource => (
                    <option key={resource} value={resource}>
                      {resource}
                    </option>
                  ))}
                </select>
                <small>The resource this permission applies to</small>
              </div>

              <div className="form-group">
                <label>Action *</label>
                <select
                  name="action"
                  value={formData.action}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Action</option>
                  {actions.map(action => (
                    <option key={action} value={action}>
                      {action}
                    </option>
                  ))}
                </select>
                <small>The action allowed by this permission</small>
              </div>

              <div className="form-group full-width">
                <label>Permission Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="e.g., users.create"
                />
                <small>Unique identifier (auto-generated from resource and action)</small>
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  disabled={loading}
                  placeholder="Brief description of what this permission allows"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Permission' : 'Create Permission')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PermissionForm;

